import {useEffect, useRef, useState} from 'react';
import {loadAndCache, readCache} from './cache';

export interface Resource<T> {
    data?: T;
    /** The last request failed. `data` may still hold an older cached value. */
    error: boolean;
}

interface State<T> extends Resource<T> {
    key: string;
}

const initialState = <T>(key: string): State<T> => ({key, data: readCache<T>(key)?.value, error: false});

/**
 * Stale-while-revalidate: returns the cached value right away (sessionStorage) and
 * reloads it in the background once it is older than CACHE_TTL_MS.
 */
export function useCachedResource<T>(key: string, load: () => Promise<T>): Resource<T> {
    const loadRef = useRef(load);
    loadRef.current = load;

    const [state, setState] = useState(() => initialState<T>(key));
    const keyChanged = state.key !== key;
    if (keyChanged) {
        setState(initialState<T>(key));
    }

    useEffect(() => {
        if (readCache<T>(key)?.fresh) return;

        let active = true;
        loadAndCache(key, () => loadRef.current())
            .then(data => {
                if (active) setState({key, data, error: false});
            })
            .catch(error => {
                console.error(`Loading "${key}" failed`, error);
                if (active) setState(previous => ({...previous, error: true}));
            });
        return () => {
            active = false;
        };
    }, [key]);

    return keyChanged ? {error: false} : state;
}
